import PropTypes from 'prop-types';
import { useLocation } from 'react-router';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { MenuOrientation, ThemeMode, NavActionType } from 'src/config';
import useConfig from 'src/hooks/useConfig';

import Dot from 'src/components/ui/Dot';
import IconButton from 'src/components/ui/IconButton';
import RouterLink from 'src/components/ui/RouterLink';
import useInterface from 'src/hooks/useInterface';
import { isMenuUrlActive } from 'src/utils/helpers/menuUrl';

const NavItem = ({
	item,
	isSelected: isSelectedProp,
	level,
	isParents = false,
	setSelectedID,
	onToggleDrawer: onToggleDrawerProp,
	drawerOpen: drawerOpenProp,
}) => {
	const { isDrawerOpen, onToggleDrawer } = useInterface();
	const handlerDrawerOpen =
		onToggleDrawerProp === undefined ? onToggleDrawer : onToggleDrawerProp;
	const drawerOpen =
		drawerOpenProp === undefined ? isDrawerOpen : drawerOpenProp;
	const theme = useTheme();
	const downLG = useMediaQuery(theme.breakpoints.down('lg'));
	const location = useLocation();

	const { mode, menuOrientation } = useConfig();
	let itemTarget = '_self';
	if (item.target) {
		itemTarget = '_blank';
	}

	const isSelected =
		isSelectedProp ?? isMenuUrlActive(item?.link || item?.url, location);

	const itemHandler = () => {
		if (downLG) handlerDrawerOpen(false);

		if (isParents && setSelectedID) {
			setSelectedID();
		}
	};

	const Icon = item.icon;
	const itemIcon = item.icon ? (
		<Icon
			style={{
				fontSize: drawerOpen ? '1rem' : '1.25rem',
				...(menuOrientation === MenuOrientation.HORIZONTAL &&
					isParents && { fontSize: 20, stroke: '1.5' }),
			}}
		/>
	) : (
		false
	);

	const textColor = mode === ThemeMode.DARK ? 'grey.400' : 'text.primary';
	const iconSelectedColor =
		mode === ThemeMode.DARK && drawerOpen ? 'text.primary' : 'primary.main';

	return (
		<>
			{menuOrientation === MenuOrientation.VERTICAL || downLG ? (
				<Box sx={{ position: 'relative' }}>
					<ListItemButton
						component={RouterLink}
						to={item.url}
						target={itemTarget}
						disabled={item.disabled}
						selected={isSelected}
						sx={{
							zIndex: 1201,
							pl: drawerOpen ? `${level * 28}px` : 1.5,
							py: !drawerOpen && level === 1 ? 1.25 : 1,
							...(drawerOpen && {
								'&:hover': {
									bgcolor:
										mode === ThemeMode.DARK
											? 'divider'
											: 'primary.lighter',
								},
								'&.Mui-selected': {
									bgcolor:
										mode === ThemeMode.DARK
											? 'divider'
											: 'primary.lighter',
									borderRight: '2px solid',
									borderColor: 'primary.main',
									color: iconSelectedColor,
									'&:hover': {
										color: iconSelectedColor,
										bgcolor:
											mode === ThemeMode.DARK
												? 'divider'
												: 'primary.lighter',
									},
								},
							}),
							...(!drawerOpen && {
								'&:hover': { bgcolor: 'transparent' },
								'&.Mui-selected': {
									'&:hover': { bgcolor: 'transparent' },
									bgcolor: 'transparent',
								},
							}),
						}}
						onClick={() => itemHandler()}
					>
						{itemIcon && (
							<ListItemIcon
								sx={{
									minWidth: 28,
									color: isSelected
										? iconSelectedColor
										: textColor,
									...(!drawerOpen && {
										borderRadius: 1.5,
										width: 36,
										height: 36,
										alignItems: 'center',
										justifyContent: 'center',
										'&:hover': {
											bgcolor:
												mode === ThemeMode.DARK
													? 'secondary.light'
													: 'secondary.lighter',
										},
									}),
									...(!drawerOpen &&
										isSelected && {
										bgcolor:
												mode === ThemeMode.DARK
													? 'primary.900'
													: 'primary.lighter',
										'&:hover': {
											bgcolor:
													mode === ThemeMode.DARK
														? 'primary.darker'
														: 'primary.lighter',
										},
									}),
								}}
							>
								{itemIcon}
							</ListItemIcon>
						)}
						{(drawerOpen || (!drawerOpen && level !== 1)) && (
							<ListItemText
								primary={
									<Typography
										variant="h6"
										sx={{
											color: isSelected
												? iconSelectedColor
												: textColor,
										}}
									>
										{item.title}
									</Typography>
								}
							/>
						)}
						{(drawerOpen || (!drawerOpen && level !== 1)) &&
							item.chip && (
							<Chip
								color={item.chip.color}
								variant={item.chip.variant}
								size={item.chip.size}
								label={item.chip.label}
								avatar={
									item.chip.avatar && (
										<Avatar>{item.chip.avatar}</Avatar>
									)
								}
							/>
						)}
					</ListItemButton>
					{(drawerOpen || (!drawerOpen && level !== 1)) &&
						item?.actions &&
						item?.actions.map((action, index) => {
							const ActionIcon = action.icon;
							const callAction = action?.function;
							return (
								<IconButton
									key={index}
									{...(action.type ===
										NavActionType.FUNCTION && {
										onClick: (event) => {
											event.stopPropagation();
											callAction();
										},
									})}
									{...(action.type === NavActionType.LINK && {
										component: RouterLink,
										to: action.url,
										target: action.target
											? '_blank'
											: '_self',
									})}
									color="secondary"
									variant="outlined"
									sx={{
										position: 'absolute',
										top: 12,
										right: 20,
										zIndex: 1202,
										width: 20,
										height: 20,
										mr: -1,
										ml: 1,
										color: 'secondary.dark',
										borderColor: isSelected
											? 'primary.light'
											: 'secondary.light',
										'&:hover': {
											borderColor: isSelected
												? 'primary.main'
												: 'secondary.main',
										},
									}}
								>
									<ActionIcon
										style={{ fontSize: '0.625rem' }}
									/>
								</IconButton>
							);
						})}
				</Box>
			) : (
				<ListItemButton
					component={RouterLink}
					to={item.url}
					target={itemTarget}
					disabled={item.disabled}
					selected={isSelected}
					onClick={() => itemHandler()}
					sx={{
						zIndex: 1201,
						...(isParents && { p: 1, mr: 1 }),
					}}
				>
					{itemIcon && (
						<ListItemIcon
							sx={{
								minWidth: 28,
								...(!drawerOpen && {
									borderRadius: 1.5,
									width: 28,
									height: 28,
									alignItems: 'center',
									justifyContent: 'flex-start',
									'&:hover': { bgcolor: 'transparent' },
								}),
								...(!drawerOpen &&
									isSelected && {
									bgcolor: 'transparent',
									'&:hover': { bgcolor: 'transparent' },
								}),
							}}
						>
							{itemIcon}
						</ListItemIcon>
					)}

					{!itemIcon && (
						<ListItemIcon
							sx={{
								color: isSelected
									? 'primary.main'
									: 'secondary.dark',
								...(!drawerOpen && {
									borderRadius: 1.5,
									alignItems: 'center',
									justifyContent: 'flex-start',
									'&:hover': { bgcolor: 'transparent' },
								}),
								...(!drawerOpen &&
									isSelected && {
									bgcolor: 'transparent',
									'&:hover': { bgcolor: 'transparent' },
								}),
							}}
						>
							<Dot
								size={4}
								color={isSelected ? 'primary' : 'secondary'}
							/>
						</ListItemIcon>
					)}
					<ListItemText
						primary={
							<Typography
								variant="h6"
								color={
									isSelected
										? 'primary.main'
										: 'secondary.dark'
								}
							>
								{item.title}
							</Typography>
						}
					/>
					{(drawerOpen || (!drawerOpen && level !== 1)) &&
						item.chip && (
						<Chip
							color={item.chip.color}
							variant={item.chip.variant}
							size={item.chip.size}
							label={item.chip.label}
							avatar={
								item.chip.avatar && (
									<Avatar>{item.chip.avatar}</Avatar>
								)
							}
						/>
					)}
				</ListItemButton>
			)}
		</>
	);
};

NavItem.propTypes = {
	setSelectedID: PropTypes.func,
	onToggleDrawer: PropTypes.func,
	item: PropTypes.object,
	level: PropTypes.number,
	isParents: PropTypes.bool,
	drawerOpen: PropTypes.bool,
	isSelected: PropTypes.bool,
};

export default NavItem;
